import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import './styles.css';

// ── Palette ────────────────────────────────────────────────────────────────
const CAT_COLORS = [
    '#1976d2','#e65100','#388e3c','#7b1fa2','#c62828',
    '#00838f','#f57f17','#4527a0','#2e7d32','#ad1457'
];

const fmt = (v, decimals = 2) =>
    Number(v).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// ── SVG Donut chart ────────────────────────────────────────────────────────
const DonutChart = ({ slices, size = 140 }) => {
    const r = 50, cx = 70, cy = 70;
    const circ = 2 * Math.PI * r;
    let offset = 0;
    const paths = slices.map((s, i) => {
        const dash = (s.pct / 100) * circ;
        const gap  = circ - dash;
        const el = (
            <circle
                key={i} cx={cx} cy={cy} r={r}
                fill="none" stroke={s.color} strokeWidth={18}
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${cx} ${cy})`}
            />
        );
        offset += dash;
        return el;
    });
    return (
        <svg width={size} height={size} viewBox="0 0 140 140">
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e8ecf0" strokeWidth={18} />
            {paths}
        </svg>
    );
};

// ── Progress bar ───────────────────────────────────────────────────────────
const ProgressBar = ({ pct, color }) => (
    <div style={{ height: 6, background: '#e8ecf0', borderRadius: 4, overflow: 'hidden', marginTop: 6 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4,
            transition: 'width .5s ease' }} />
    </div>
);

/**
 * CostWidget — beautiful, interactive BOQ with donut chart + drill-down
 */
const CostWidget = ({ data, onComplete }) => {
    const [hierarchicalData, setHierarchicalData] = useState([]);
    const [totalCost, setTotalCost] = useState(0);
    const [selectedCategory, setSelectedCategory]     = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError]         = useState(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (data && data.response) parseAndProcessData(data.response);
    }, [data]);

    // ── CSV parsing ──────────────────────────────────────────────────────
    const parseAndProcessData = (csvData) => {
        try {
            Papa.parse(csvData.trim(), {
                header: true, skipEmptyLines: true,
                transformHeader: h => h.trim(),
                complete: (results) => {
                    if (!results.data?.length) { setError('No cost data found'); setIsLoading(false); return; }
                    const processed = buildHierarchy(results.data);
                    setHierarchicalData(processed.hierarchicalData);
                    setTotalCost(processed.totalCost);
                    setIsLoading(false);
                    if (onComplete) onComplete(data.descriptiveResponse || 'Cost data rendered');
                },
                error: () => { setError('Failed to parse cost data'); setIsLoading(false); }
            });
        } catch { setError('Failed to process cost data'); setIsLoading(false); }
    };

    const buildHierarchy = (rows) => {
        const catMap = new Map();
        let grandTotal = 0;
        rows.forEach(row => {
            const category    = (row['Cost Category']    || 'Uncategorized').toString().trim();
            const subCategory = (row['Cost Sub-category']|| 'General').toString().trim();
            const element     = (row['Cost Element']     || '').toString().trim();
            const rate        = (row['Rate (per unit)']  || '0').toString().trim();
            const quantity    = (row['Quantity']         || '1').toString().trim();
            const unitType    = (row['Unit Type']        || '').toString().trim();
            const currency    = (row['Currency']         || '').toString().trim();
            let netCost = parseFloat(row['Net Cost'] || 0);
            if (!netCost || isNaN(netCost)) netCost = (parseFloat(rate)||0) * (parseFloat(quantity)||1);
            if (!element && netCost === 0) return;
            grandTotal += netCost;
            if (!catMap.has(category)) catMap.set(category, { category, totalCost: 0, subCategories: new Map() });
            const cat = catMap.get(category);
            cat.totalCost += netCost;
            if (!cat.subCategories.has(subCategory))
                cat.subCategories.set(subCategory, { subCategory, totalCost: 0, elements: [] });
            const sub = cat.subCategories.get(subCategory);
            sub.totalCost += netCost;
            sub.elements.push({ element, rate, quantity, unitType, netCost, currency });
        });
        const hierarchicalData = Array.from(catMap.values()).map((cat, i) => {
            cat.color = CAT_COLORS[i % CAT_COLORS.length];
            cat.percentage = grandTotal > 0 ? ((cat.totalCost / grandTotal) * 100).toFixed(1) : '0.0';
            cat.subCategories = Array.from(cat.subCategories.values()).map(sub => {
                sub.percentage = cat.totalCost > 0 ? ((sub.totalCost / cat.totalCost) * 100).toFixed(1) : '0.0';
                return sub;
            });
            return cat;
        });
        return { hierarchicalData, totalCost: grandTotal };
    };

    // ── Inline editing ───────────────────────────────────────────────────
    const handleInputChange = (elemIdx, field, value) => {
        const updated = [...hierarchicalData];
        const ci = updated.findIndex(c => c === selectedCategory);
        const si = updated[ci].subCategories.findIndex(s => s === selectedSubCategory);
        const el = updated[ci].subCategories[si].elements[elemIdx];
        if (field === 'rate')     el.rate     = value;
        if (field === 'quantity') el.quantity = value;
        el.netCost = (parseFloat(el.rate)||0) * (parseFloat(el.quantity)||1);
        const sub = updated[ci].subCategories[si];
        sub.totalCost = sub.elements.reduce((s, e) => s + (parseFloat(e.netCost)||0), 0);
        const cat = updated[ci];
        cat.totalCost = cat.subCategories.reduce((s, sc) => s + sc.totalCost, 0);
        const newTotal = updated.reduce((s, c) => s + c.totalCost, 0);
        updated.forEach(c => {
            c.percentage = newTotal > 0 ? ((c.totalCost / newTotal) * 100).toFixed(1) : '0.0';
            c.subCategories.forEach(sc => {
                sc.percentage = c.totalCost > 0 ? ((sc.totalCost / c.totalCost) * 100).toFixed(1) : '0.0';
            });
        });
        setHierarchicalData(updated);
        setTotalCost(newTotal);
        setSelectedCategory(updated[ci]);
        setSelectedSubCategory(updated[ci].subCategories[si]);
    };

    if (isLoading) return <div className="archie-widget-loading">Loading cost data…</div>;
    if (error)     return <div className="archie-widget-error">{error}</div>;
    if (!hierarchicalData.length) return <div className="archie-widget-error">No cost data available</div>;

    const donutSlices = hierarchicalData.map(c => ({ pct: parseFloat(c.percentage), color: c.color }));
    const currency = hierarchicalData[0]?.subCategories?.[0]?.elements?.[0]?.currency || 'USD';

    // ── Breadcrumb ────────────────────────────────────────────────────────
    const Breadcrumb = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#90a4ae', marginBottom: 14 }}>
            <span
                role="button"
                tabIndex={0}
                style={{ cursor: selectedCategory ? 'pointer' : 'default',
                    color: selectedCategory ? '#1976d2' : '#90a4ae', fontWeight: 600 }}
                onClick={() => { setSelectedCategory(null); setSelectedSubCategory(null); }}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (setSelectedCategory(null) || setSelectedSubCategory(null))}
            >BOQ</span>
            {selectedCategory && <>
                <span>›</span>
                <span
                    role="button"
                    tabIndex={0}
                    style={{ cursor: selectedSubCategory ? 'pointer' : 'default',
                        color: selectedSubCategory ? '#1976d2' : '#546e7a', fontWeight: 600 }}
                    onClick={() => setSelectedSubCategory(null)}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setSelectedSubCategory(null)}
                >{selectedCategory.category}</span>
            </>}
            {selectedSubCategory && <>
                <span>›</span>
                <span style={{ color: '#546e7a', fontWeight: 600 }}>{selectedSubCategory.subCategory}</span>
            </>}
        </div>
    );

    return (
        <div ref={containerRef}
            style={{ fontFamily: '"Suisse Intl", sans-serif', padding: '16px 4px', color: '#263238' }}>

            {/* ── Header ────────────────────────────────────────────────── */}
            <div style={{
                background: 'linear-gradient(135deg,#1a237e 0%,#283593 100%)',
                borderRadius: 14, padding: '20px 24px', marginBottom: 20,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 16, boxShadow: '0 6px 20px rgba(26,35,126,.35)'
            }}>
                <div>
                    <div style={{ color: '#90caf9', fontSize: 12, fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 4 }}>
                        💰 Bill of Quantities
                    </div>
                    <div style={{ color: '#fff', fontSize: 26, fontWeight: 700 }}>
                        {currency} {fmt(totalCost)}
                    </div>
                    <div style={{ color: '#90caf9', fontSize: 12, marginTop: 4 }}>
                        {hierarchicalData.length} categories · {
                            hierarchicalData.reduce((s, c) => s + c.subCategories.length, 0)
                        } line items
                    </div>
                </div>
                <DonutChart slices={donutSlices} />
            </div>

            <Breadcrumb />

            {/* ── LEVEL 1: Category cards ──────────────────────────────── */}
            {!selectedCategory && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {hierarchicalData.map((cat, i) => (
                        <div
                            key={i}
                            onClick={() => setSelectedCategory(cat)}
                            role="button" tabIndex={0}
                            onKeyDown={e => (e.key==='Enter'||e.key===' ') && setSelectedCategory(cat)}
                            style={{
                                background: '#fff', border: `2px solid ${cat.color}20`,
                                borderLeft: `5px solid ${cat.color}`,
                                borderRadius: 10, padding: '14px 18px', cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0,0,0,.06)',
                                transition: 'transform .15s,box-shadow .15s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform='translateX(4px)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,.12)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,.06)'; }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 700, fontSize: 14, color: '#263238' }}>{cat.category}</span>
                                <span style={{ fontWeight: 700, fontSize: 15, color: cat.color }}>
                                    {currency} {fmt(cat.totalCost)}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                                <span style={{ fontSize: 12, color: '#90a4ae' }}>
                                    {cat.subCategories.length} sub-items &nbsp;·&nbsp; click to expand
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: cat.color }}>{cat.percentage}%</span>
                            </div>
                            <ProgressBar pct={parseFloat(cat.percentage)} color={cat.color} />
                        </div>
                    ))}
                </div>
            )}

            {/* ── LEVEL 2: Sub-category cards ─────────────────────────── */}
            {selectedCategory && !selectedSubCategory && (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: `${selectedCategory.color}12`, borderRadius: 10,
                        padding: '12px 16px', marginBottom: 14 }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: selectedCategory.color }}>
                            {selectedCategory.category}
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 14, color: selectedCategory.color }}>
                            {currency} {fmt(selectedCategory.totalCost)}
                        </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {selectedCategory.subCategories.map((sub, i) => (
                            <div
                                key={i}
                                onClick={() => setSelectedSubCategory(sub)}
                                role="button" tabIndex={0}
                                onKeyDown={e => (e.key==='Enter'||e.key===' ') && setSelectedSubCategory(sub)}
                                style={{
                                    background: '#fff', borderRadius: 10, padding: '14px 18px',
                                    cursor: 'pointer', border: '1px solid #e0e0e0',
                                    boxShadow: '0 2px 8px rgba(0,0,0,.05)',
                                    transition: 'transform .15s,box-shadow .15s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.transform='translateX(4px)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,.1)'; }}
                                onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,.05)'; }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, fontSize: 14, color: '#37474f' }}>{sub.subCategory}</span>
                                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1976d2' }}>
                                        {currency} {fmt(sub.totalCost)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                                    <span style={{ fontSize: 12, color: '#90a4ae' }}>
                                        {sub.elements.length} elements &nbsp;·&nbsp; click to view
                                    </span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: selectedCategory.color }}>
                                        {sub.percentage}% of {selectedCategory.category}
                                    </span>
                                </div>
                                <ProgressBar pct={parseFloat(sub.percentage)} color={selectedCategory.color} />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── LEVEL 3: Element table ───────────────────────────────── */}
            {selectedSubCategory && (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: `${selectedCategory.color}12`, borderRadius: 10,
                        padding: '12px 16px', marginBottom: 16 }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: selectedCategory.color }}>
                            {selectedSubCategory.subCategory}
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 14, color: selectedCategory.color }}>
                            {currency} {fmt(selectedSubCategory.totalCost)}
                        </span>
                    </div>

                    {/* Metric summary chips */}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                        {[
                            { label: 'Items',      value: selectedSubCategory.elements.length },
                            { label: 'Total Cost', value: `${currency} ${fmt(selectedSubCategory.totalCost)}` },
                            { label: '% of Category', value: `${selectedSubCategory.percentage}%` }
                        ].map((m, i) => (
                            <div key={i} style={{
                                background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
                                padding: '8px 14px', boxShadow: '0 1px 4px rgba(0,0,0,.06)'
                            }}>
                                <div style={{ fontSize: 10, color: '#90a4ae', textTransform: 'uppercase',
                                    letterSpacing: '0.5px', fontWeight: 600 }}>{m.label}</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#1a237e', marginTop: 2 }}>{m.value}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ overflowX: 'auto', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,.07)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' }}>
                            <thead>
                                <tr style={{ background: '#1a237e' }}>
                                    {['Cost Element','Rate / unit','Quantity','Unit','Net Cost ('+currency+')'].map(h => (
                                        <th key={h} style={{ padding: '11px 12px', textAlign: 'left',
                                            color: '#fff', fontSize: 11, fontWeight: 700,
                                            textTransform: 'uppercase', letterSpacing: '0.4px',
                                            whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {selectedSubCategory.elements.map((el, idx) => (
                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fafafa' : '#fff',
                                        borderBottom: '1px solid #e8ecf0' }}>
                                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#37474f' }}>
                                            {el.element}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            {el.rate?.toString().endsWith('%') ? (
                                                <span style={{ color: '#1976d2', fontWeight: 600 }}>{el.rate}</span>
                                            ) : (
                                                <input
                                                    type="number" value={el.rate}
                                                    onChange={e => handleInputChange(idx, 'rate', e.target.value)}
                                                    style={{ width: 75, padding: '4px 6px', border: '1px solid #b0bec5',
                                                        borderRadius: 5, fontSize: 13, color: '#263238' }}
                                                />
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <input
                                                type="number" value={el.quantity}
                                                onChange={e => handleInputChange(idx, 'quantity', e.target.value)}
                                                style={{ width: 65, padding: '4px 6px', border: '1px solid #b0bec5',
                                                    borderRadius: 5, fontSize: 13, color: '#263238' }}
                                            />
                                        </td>
                                        <td style={{ padding: '10px 12px', color: '#78909c' }}>{el.unitType}</td>
                                        <td style={{ padding: '10px 12px', fontWeight: 700, color: '#1a237e',
                                            textAlign: 'right' }}>
                                            {fmt(el.netCost)}
                                        </td>
                                    </tr>
                                ))}
                                <tr style={{ background: '#e8eaf6', borderTop: '2px solid #9fa8da' }}>
                                    <td colSpan={4} style={{ padding: '11px 12px', fontWeight: 700,
                                        color: '#1a237e', textTransform: 'uppercase', fontSize: 12 }}>
                                        Sub-total
                                    </td>
                                    <td style={{ padding: '11px 12px', fontWeight: 700, color: '#1a237e',
                                        textAlign: 'right', fontSize: 15 }}>
                                        {fmt(selectedSubCategory.totalCost)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CostWidget;

